import { FunctionComponent, MouseEvent, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useContactSupportPermissionGet } from '@sbv-clients/data-access-api';
import { useAuth } from '@sbv-clients/shared-feature-auth';
import {
  Icon,
  LetterAvatar,
  ListItemIcon,
  Menu,
  MenuItem,
  printErrorToConsole,
  Typography,
  useAccessToken,
  useConfirmationModal,
  useLoading,
  useTheme,
} from '@sbv-clients/ui-shared';

import { UserDropdownProps } from './typings/user-dropdown-props';

export const UserDropdown: FunctionComponent<UserDropdownProps> = ({
  showPreferences,
  ssoUserName,
  showResetConfiguration,
  profileImage,
  showRemoteSupport,
  showContactUs,
  extraMenuItems,
}) => {
  const {
    isLoading: signOutLoading,
    showLoading: showSignOutLoading,
    hideLoading: hideSignOutLoading,
  } = useLoading();
  const { username, onSignOut } = useAuth();
  const displayUsername = ssoUserName || username;
  const navigate = useNavigate();
  const [anchorMenu, setAnchorMenu] = useState<Element | null>(null);
  const toggleMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorMenu(event.currentTarget);
  }, []);

  const closeMenu = useCallback(() => {
    setAnchorMenu(null);
  }, []);

  const theme = useTheme();

  const handleClickPreferences = useCallback(() => {
    closeMenu();
    navigate('/employees/preferences');
  }, [closeMenu, navigate]);

  const { data: contactSupportPermission } = useContactSupportPermissionGet({
    enabled: Boolean(showContactUs),
  });

  const handleClickContactUs = useCallback(() => {
    closeMenu();
    navigate('/contact-us');
  }, [closeMenu, navigate]);

  const handleSignOut = useCallback(async () => {
    try {
      showSignOutLoading();
      await onSignOut();
    } catch (e) {
      /* empty */
    } finally {
      hideSignOutLoading();
    }
  }, [hideSignOutLoading, onSignOut, showSignOutLoading]);
  const confirmationModal = useConfirmationModal();

  const { persistSessionToken } = useAccessToken();

  const getNewConfig = () => {
    persistSessionToken();
    window.location.reload();
  };

  const openCleanConfigModal = async () => {
    await confirmationModal
      .openModal({
        title: 'Refresh configuration',
        content:
          'Are you sure you want to refresh the configuration? This will overwrite your current configuration with the latest changes',
      })
      .then(getNewConfig)
      .catch(printErrorToConsole);
  };

  return (
    <>
      <LetterAvatar
        name={displayUsername}
        sx={{
          width: '32px',
          height: '32px',
          background: theme.colors.primary.main,
          color: theme.background.white,
        }}
        onClick={toggleMenu}
        src={profileImage}
      />
      <Menu
        anchorEl={anchorMenu}
        open={Boolean(anchorMenu)}
        onClose={closeMenu}
        sx={{ marginRight: '20px', paddingRight: '20px' }}
      >
        <MenuItem onClick={closeMenu}>
          <ListItemIcon>
            <Icon
              name={'Account'}
              customSizes={{
                width: 24,
                height: 24,
                color: theme.text.darkText,
              }}
            />
          </ListItemIcon>
          <Typography fontWeight="bold">{displayUsername}</Typography>
        </MenuItem>
        {showPreferences && (
          <MenuItem onClick={handleClickPreferences}>
            <ListItemIcon>
              <Icon
                name={'SettingsRounded'}
                customSizes={{ width: 24, height: 24 }}
              />
            </ListItemIcon>
            <Typography fontWeight="bold">Preferences</Typography>
          </MenuItem>
        )}
        {showResetConfiguration && (
          <MenuItem onClick={openCleanConfigModal}>
            <ListItemIcon>
              <Icon name={'RefreshRounded'} />
            </ListItemIcon>
            <Typography fontWeight="bold">Refresh Configuration</Typography>
          </MenuItem>
        )}
        {showRemoteSupport && (
          <MenuItem
            onClick={() => window.open('https://sbv.screenconnect.com/')}
          >
            <ListItemIcon>
              <Icon name={'SupportRounded'} />
            </ListItemIcon>
            <Typography fontWeight="bold">Remote Support</Typography>
          </MenuItem>
        )}
        {showContactUs && contactSupportPermission?.canContactSupport && (
          <MenuItem onClick={handleClickContactUs}>
            <ListItemIcon>
              <Icon
                name={'ChatBubbleOutlineRounded'}
                customSizes={{ width: 24, height: 24 }}
              />
            </ListItemIcon>
            <Typography fontWeight="bold">Contact Us</Typography>
          </MenuItem>
        )}
        {extraMenuItems?.map(({ label, icon, onClick }) => (
          <MenuItem
            key={label}
            onClick={() => {
              closeMenu();
              onClick();
            }}
          >
            <ListItemIcon>{icon}</ListItemIcon>
            <Typography fontWeight="bold">{label}</Typography>
          </MenuItem>
        ))}
        {confirmationModal.modal}
        <MenuItem onClick={handleSignOut} disabled={signOutLoading}>
          <ListItemIcon>
            <Icon
              name={'Logout'}
              fontSize="small"
              customSizes={{ width: 24, height: 24 }}
            />
          </ListItemIcon>
          <Typography fontWeight="bold">Sign Out</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};
